import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { SxProps } from "@mui/material/styles";
import type { SwitchProps } from "@mui/material/Switch";
import BaseSwitch from "@mui/material/Switch";
import type { Overwrite } from "@mui/types";
import { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { mergeSxProps } from "@polifonia/utils/mui/mergeSxProps";

const sxPropsRecord = {
  Switch: {
    width: 40,
    height: 20,
    padding: 0,
    "& .MuiSwitch-track:hover, & .MuiSwitch-switchBase:hover + .MuiSwitch-track":
      {
        backgroundColor: theme.colors.neutral050,
      },
    "& .MuiSwitch-switchBase.Mui-checked:hover + .MuiSwitch-track": {
      backgroundColor: theme.colors.blue050,
    },
    "& .MuiSwitch-track:active, & .MuiSwitch-switchBase:active + .MuiSwitch-track":
      {
        backgroundColor: theme.colors.neutral070,
      },
    "& .MuiSwitch-switchBase.Mui-checked:active + .MuiSwitch-track": {
      backgroundColor: theme.colors.blue070,
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 16,
      height: 16,
      boxShadow: "0px 2px 4px 0px rgba(0, 35, 11, 0.2)",
    },
    "&:has(.MuiSwitch-switchBase.Mui-focusVisible)": {
      boxShadow: `0 0 0 3px ${theme.colors.blue030}`,
      borderRadius: 10,
    },
    "& .MuiSwitch-track": {
      borderRadius: 10,
      backgroundColor: theme.colors.neutral040,
      opacity: 1,
      transition: muiTheme.transitions.create(["background-color"], {
        duration: 500,
      }),
    },
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: "2px",
      transitionDuration: "300ms",
      "&.Mui-checked": {
        transform: "translateX(20px)",
        color: theme.colors.white,
        "& + .MuiSwitch-track": {
          backgroundColor: theme.colors.blue060,
          opacity: 1,
          border: 0,
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          cursor: "not-allowed",
          backgroundColor: theme.colors.blue030,
        },
      },
      "&.Mui-disabled": {
        "& .MuiSwitch-thumb": {
          color: theme.colors.white,
        },
        "& + .MuiSwitch-track": {
          cursor: "not-allowed",
          opacity: 1,
          backgroundColor: theme.colors.neutral020,
        },
      },
    },
  },
  loadingSwitch: {
    "& .MuiSwitch-track": {
      backgroundColor: theme.colors.neutral020,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: theme.colors.blue030,
    },
  },
  loadingIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: theme.colors.white,
  },
} satisfies SxProps<typeof muiTheme>;

const LoadingIcon: React.FC<{ loading: boolean }> = (props) => {
  return (
    <Box sx={sxPropsRecord.loadingIcon}>
      {props.loading && (
        <CircularProgress size={12} sx={{ color: theme.colors.neutral020 }} />
      )}
    </Box>
  );
};

namespace Switch {
  export type CustomProps = {
    loading?: boolean;
  };
  export interface Props extends Overwrite<SwitchProps, CustomProps> {}
}

const Switch: React.FC<Switch.Props> = ({ loading = false, ...props }) => (
  <BaseSwitch
    sx={mergeSxProps(
      sxPropsRecord.Switch,
      loading ? sxPropsRecord.loadingSwitch : null,
    )}
    disableRipple
    icon={<LoadingIcon loading={loading} />}
    checkedIcon={<LoadingIcon loading={loading} />}
    focusVisibleClassName=".Mui-focusVisible"
    {...props}
  />
);

export { Switch };
